import CustomButton from 'components/CustomInput/Button';
import React, { useEffect, useState } from 'react';
import TextHeader from 'components/CustomText/TextHeader';
import { Checkbox } from '@material-ui/core';
import TextSubHeader from 'components/CustomText/TextSubHeader';
import CustomTextField from 'components/Input/CustomTextField';
import CustomDatePicker from 'components/Input/CustomDatePicker';
import CustomTable from 'components/CustomTable';
import PopupInput from 'components/Application/TeamMemberForm/PopupInput';
import useUploadDocData from './useUploadDocData';
import GeneralPostApprovalLayout from 'components/GeneralPostApprovalLayout';
import CustomRadio from 'components/CustomRadio';
import { postApprovalPermissionChecking, produce, waitUntilFormUpdate } from 'utility/general';
import { SEvenRow } from 'style/CommonStyledComponent';
import useQuery from 'hooks/useQuery';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useDialog from 'components/ConfirmDialog/useDialog';
import { GET_FINAL_REPORT, SAVE_FINAL_REPORT } from 'store/key';
import { FINAL_REPORT } from 'utility/postApprovalLookUp';
import moment from 'moment';
import useSeriousAdverseEventsData from './useSeriousAdverseEventsData';

import styled from 'styled-components';
import {
    ERROR_MAX_LENGTH,
    GENERAL_RE_SUBMIT_SUCCESS,
    GENERAL_SAVE_SUCCESS,
    GENERAL_SUBMIT_SUCCESS,
    PLEASE_COMPLETE_THE_FORM,
} from 'utility/frontendMessage';
import { setGlobalLoading } from 'store/global/reducer';

const SSection = styled.div`
    margin-top: 20px;
`;

const SSmallSection = styled.div`
    margin-top: 10px;
`;

const FinalReport = (props) => {
    const appId = useQuery('appId');
    const siteId = useQuery('siteId');
    const reportId = useQuery('reportId');
    const finalReport = useSelector((state) => state.finalReport);
    const status = finalReport?.remoteDetail?.lastStatus;
    const history = useHistory();
    const showDialog = useDialog();
    const dispatch = useDispatch();
    const signedFileId = finalReport.remoteDetail.signedFileId;
    const signedFileName = finalReport.remoteDetail.signedFileName;

    const [signedUpload, setSignedUpload] = useState({ key: null, fileName: '' });
    const [validation, setValidation] = useState({});
    const [postData, setPostData] = useState({});
    const [forceErrorDisplay, setForceErrorDisplay] = useState(false);
    const [removeRequest, setRemoveRequest] = useState([]);

    const [maxLengthValidation, setMaxLengthValidation] = useState({});

    const isPermission = postApprovalPermissionChecking({ status, reportData: finalReport.remoteDetail });

    const {
        columns: uploadDocColumns,
        rows: uploadDocRows,
        defaultUploadDocFormList,
        onUploadDocEditSubmit,
        handleUploadDocUpdate,
        handleUploadDocDelete,
        showUploadDocDialog,
        setShowUploadDocDialog,
        addSupplements,
        deleteSupplements,
        resetUploadData,
    } = useUploadDocData({ isPermission, data: finalReport.remoteDetail });

    const {
        columns: adverseEventsColumns,
        rows: adverseEventsRows,
        defaultAdverseEventsFormList,
        selectedAdverseEventsRow,
        onAdverseEventsEditSubmit,
        handleAdverseEventsRecordUpdate,
        handleAdverseEventsDelete,
        handleAdverseEventsEditClick,
        showAdverseEventsDialog,
        setShowAdverseEventsDialog,
        setSelectedAdverseEventsRow,
        newSummaryOfLocalSeriousAdverseEvents,
        updateSummaryOfLocalSeriousAdverseEvents,
        deleteSummaryOfLocalSeriousAdverseEvents,
        resetTableData,
    } = useSeriousAdverseEventsData({ data: finalReport.remoteDetail });

    useEffect(() => {
        dispatch({
            type: GET_FINAL_REPORT,
            payload: {
                appId,
                siteId,
                docType: FINAL_REPORT,
                reportId,
                onPermissionError: () => {
                    history.push('/');
                },
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (signedFileId || signedFileName) {
            setSignedUpload({ fileName: signedFileName, key: signedFileId });
        } else {
            setSignedUpload({ fileName: '', key: null });
        }
    }, [signedFileId, signedFileName]);

    const clearKey = (keys, timeout = 300) => {
        // wait for debounceChange interval
        removeKey(keys);
        setTimeout(() => {
            removeKey(keys);
        }, 300);
    };

    const removeKey = (keys) => {
        setPostData(
            produce((draft) => {
                keys.forEach((item) => {
                    delete draft[item];
                });
            }),
        );

        setRemoveRequest(
            produce((draft) => {
                keys.forEach((item) => {
                    if (!draft.includes(item)) {
                        draft.push(item);
                    }
                });
            }),
        );
    };

    const checkNullOrUndefined = (value) => {
        if (value === undefined || value === null) {
            return true;
        }

        return false;
    };

    const getValue = (key) => {
        if (!postData[key] && removeRequest.includes(key)) {
            return null;
        }

        return postData?.[key] ?? finalReport?.remoteDetail?.[key];
    };

    const updateValue = (key, value) => {
        setPostData(
            produce((draft) => {
                draft[key] = value;
            }),
        );
    };

    const updateValidation = (key, isValid) => {
        setValidation(
            produce((draft) => {
                draft[key] = isValid;
            }),
        );
    };

    const getRemoveRequest = () => {
        const updatedRemoveRequest = produce(removeRequest, (draft) => {
            Object.keys(postData).forEach((key) => {
                const index = draft.findIndex((item) => item === key);

                if (index !== -1) {
                    draft.splice(index, 1);
                }
            });
        });

        return updatedRemoveRequest;
    };

    const updateMaxLength = (key, value) => {
        setMaxLengthValidation(
            produce((draft) => {
                draft[key] = value;
            }),
        );
    };

    const getSignedUpload = () => {
        if (signedUpload.isNewUpload) {
            return signedUpload;
        }

        return {
            key: finalReport?.remoteDetail?.signedFileId,
            fileName: finalReport?.remoteDetail?.signedFileName,
        };
    };

    const onFormSave = ({ isSubmit = false, isResubmit, callback }) => {
        // let message = '';

        // if (!isSubmit) {
        //     message = GENERAL_SAVE_TIPS;
        // } else if (isResubmit) {
        //     message = GENERAL_RE_SUBMIT_TIPS;
        // } else if (!isResubmit) {
        //     message = GENERAL_SUBMIT_TIPS;
        // }

        // const isConfirm = showDialog({
        //     description: message,
        //     disableCancelButton: true,
        // });

        // if (!isConfirm) {
        //     return;
        // }

        const saveData = postData;
        saveData.addSupplements = addSupplements;
        saveData.deleteSupplements = deleteSupplements;
        saveData.newSummaryOfLocalSeriousAdverseEvents = newSummaryOfLocalSeriousAdverseEvents.map((item) => {
            delete item['id'];
            return item;
        });
        saveData.updateSummaryOfLocalSeriousAdverseEvents = updateSummaryOfLocalSeriousAdverseEvents;
        saveData.deleteSummaryOfLocalSeriousAdverseEvents = deleteSummaryOfLocalSeriousAdverseEvents;

        if (signedUpload.isNewUpload && signedUpload.fileName) {
            saveData.signedFileId = parseInt(signedUpload.key);
            saveData.signedFileName = signedUpload.fileName;
        }

        saveData.removeRequest = getRemoveRequest();

        // case of no draft version
        if (!reportId) {
            saveData.siteInfoId = parseInt(siteId);
            saveData.appId = parseInt(appId);
        } else {
            saveData.reportId = parseInt(reportId);
            saveData.appId = finalReport.remoteDetail.appId;
        }

        // handle not yet start logic, if value is null, set the value to false to server
        if (postData?.['isNotYetStarted'] === undefined && finalReport?.remoteDetail?.['isNotYetStarted'] === null) {
            saveData['isNotYetStarted'] = false;
        }

        setGlobalLoading(true);

        dispatch({
            type: SAVE_FINAL_REPORT,
            payload: {
                data: saveData,
                isSubmit,
                onError: () => {
                    setGlobalLoading(false);
                },
                callback: (resultReportId) => {
                    // case when save
                    if (!isSubmit) {
                        if (!reportId) {
                            history.replace(`/final-report?reportId=${resultReportId}`);
                        }

                        setTimeout(() => {
                            dispatch({
                                type: GET_FINAL_REPORT,
                                payload: {
                                    appId,
                                    siteId,
                                    docType: FINAL_REPORT,
                                    reportId: resultReportId,
                                    isLoading: false,
                                    callback: () => {
                                        callback?.(resultReportId);
                                    },
                                    final: () => {
                                        setGlobalLoading(false);

                                        // if callback exist, it should be print action.
                                        if (!callback) {
                                            showDialog({
                                                disableCancelButton: true,
                                                description: GENERAL_SAVE_SUCCESS,
                                            });
                                        }

                                        setPostData({});
                                        setRemoveRequest([]);
                                        resetTableData();
                                        resetUploadData();
                                        setSignedUpload({ key: 0, fileName: '' });
                                    },
                                },
                            });
                        }, 100);
                    } else if (isSubmit) {
                        setGlobalLoading(false);
                        showDialog({
                            disableCancelButton: true,
                            description: finalReport.remoteDetail.hasCCOAction
                                ? GENERAL_RE_SUBMIT_SUCCESS
                                : GENERAL_SUBMIT_SUCCESS,
                        });
                        history.push('/');
                    }
                },
            },
        });
    };

    const detectChanges = () => {
        let hasChange = false;

        if (Object.values(postData).length > 0) {
            hasChange = true;
        }

        if (addSupplements.length > 0 || deleteSupplements.length > 0) {
            hasChange = true;
        }

        if (
            removeRequest.length > 0 ||
            newSummaryOfLocalSeriousAdverseEvents.length > 0 ||
            updateSummaryOfLocalSeriousAdverseEvents.length > 0 ||
            deleteSummaryOfLocalSeriousAdverseEvents.length > 0
        ) {
            hasChange = true;
        }

        if (signedUpload.isNewUpload) {
            hasChange = true;
        }

        return hasChange;
    };

    useEffect(() => {
        let valid = adverseEventsRows.length > 0 ? true : false;
        if (getValue('mandatorySeriousAdverseEvents') === true) {
            updateValidation('mandatorySeriousAdverseEventsTable', valid);
        } else {
            updateValidation('mandatorySeriousAdverseEventsTable', true);
        }
    }, [getValue('mandatorySeriousAdverseEvents')]);

    return (
        <GeneralPostApprovalLayout
            type={FINAL_REPORT}
            isLoading={finalReport?.isLoading}
            isFormCreate={!reportId}
            status={finalReport?.remoteDetail?.lastStatus}
            reportId={reportId}
            hasCCOAction={finalReport?.remoteDetail?.hasCCOAction}
            isPermission={isPermission}
            isViewOnly={!isPermission}
            signedUpload={getSignedUpload()}
            checkIsFormDataChanged={detectChanges}
            checkSubmitFormValid={async () => {
                const hasChange = await waitUntilFormUpdate();

                if (hasChange) {
                    return;
                }

                setForceErrorDisplay(true);

                if (
                    validation['mandatorySeriousAdverseEventsTable'] === false &&
                    getValue('mandatorySeriousAdverseEvents') === true
                ) {
                    return { valid: false, msg: 'Please add event data for Q3.' };
                }

                const hasError = Object.values(validation).includes(false);

                if (hasError) {
                    return { valid: false, msg: PLEASE_COMPLETE_THE_FORM };
                }

                return { valid: true };
            }}
            onSubmit={async ({ isReSubmit }) => {
                onFormSave({ isSubmit: true, isReSubmit });
            }}
            onCreate={async ({ callback }) => {
                onFormSave({ isSubmit: false, isReSubmit: false, callback });
            }}
            checkSaveFormValid={async () => {
                // handling for debounceChange, if form is changing, wait for 300ms
                const hasChange = await waitUntilFormUpdate();

                if (hasChange) {
                    return;
                }

                const hasMaxLengthError = Object.values(maxLengthValidation).includes(false);

                if (hasMaxLengthError) {
                    return { valid: false, msg: ERROR_MAX_LENGTH };
                }

                return { valid: true };
            }}
            onSignedDocumentUploaded={(result) => {
                // result is in format {fileName: 'test.docx', key: 112, isNewUpload: true, file: FileObject}

                // file is removed
                if (!result.fileName) {
                    clearKey(['signedFileId', 'signedFileName']);
                    result.isNewUpload = true;
                    result.fileName = null;
                    result.key = null;
                }

                setSignedUpload(result);
            }}
            siteInfo={getValue('siteInstruction')}
        >
            {showUploadDocDialog && (
                <PopupInput
                    title={'Upload Supplementary Document'}
                    height="500px"
                    width="700px"
                    alignItems={'center'}
                    defaultFormList={defaultUploadDocFormList}
                    editRow={onUploadDocEditSubmit}
                    setOpenPopup={setShowUploadDocDialog}
                    tableList={uploadDocRows}
                    onSubmit={handleUploadDocUpdate}
                    isUpload
                />
            )}
            {showAdverseEventsDialog && (
                <PopupInput
                    title={'Summary of Local Serious Adverse Events'}
                    height="500px"
                    width="750px"
                    alignItems={'center'}
                    defaultFormList={defaultAdverseEventsFormList}
                    selectedRow={selectedAdverseEventsRow}
                    editRow={onAdverseEventsEditSubmit}
                    setOpenPopup={setShowAdverseEventsDialog}
                    tableList={adverseEventsRows}
                    onSubmit={handleAdverseEventsRecordUpdate}
                />
            )}
            <>
                <TextHeader style={{ textAlign: 'center', marginBottom: 10 }}>Research Final Report Form</TextHeader>
                <div style={{ marginTop: 20 }}>
                    <TextSubHeader>1. Research Identification</TextSubHeader>

                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div>Scientific Title:</div>
                            <div>{getValue('scientificTitle')}</div>
                        </div>
                    </SEvenRow>
                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div>IRB/REC Ref. No.:</div>
                            <div>{getValue('refNo')}</div>
                        </div>
                        <div>
                            <div>Coordinating Investigator:</div>
                            <div>{getValue('ci')}</div>
                        </div>
                    </SEvenRow>
                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div>Protocol No.:</div>
                            <div>{getValue('protocolNo')}</div>
                        </div>
                        <div>
                            <div>Anticipated Study End Date:</div>
                            <div>
                                {getValue('anticipatedStudyEndDate')
                                    ? moment(getValue('anticipatedStudyEndDate')).format('YYYY-MM-DD')
                                    : getValue('anticipatedStudyEndDate')}
                            </div>
                        </div>
                    </SEvenRow>
                </div>
                <div style={{ marginTop: 20 }}>
                    <TextSubHeader>2. Final Report</TextSubHeader>

                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div style={{ display: 'flex' }}>Actual Study Start Date:</div>
                            <div style={{ display: 'flex' }}>
                                <div style={{ width: 150 }}>
                                    <CustomDatePicker
                                        disabled={!isPermission || getValue('isNotYetStarted')}
                                        value={getValue('actualStudyStartDate')}
                                        validators={!getValue('isNotYetStarted') ? ['isRequired'] : []}
                                        forceErrorDisplay={forceErrorDisplay}
                                        isValidValue={(isValid) => {
                                            updateValidation('actualStudyStartDate', isValid);
                                        }}
                                        minDate={new Date(finalReport?.remoteDetail?.decisionDate).getTime()}
                                        maxDate={new Date().getTime()}
                                        onChange={(date) => {
                                            updateValue('actualStudyStartDate', date);
                                        }}
                                    />
                                </div>
                                <div style={{ width: 20, margin: '0 10px' }}>
                                    <Checkbox
                                        style={{ padding: 0 }}
                                        disabled={!isPermission}
                                        checked={getValue('isNotYetStarted')}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                clearKey(['actualStudyStartDate', 'actualStudyEndDate']);
                                            }
                                            updateValue('isNotYetStarted', e.target.checked);
                                        }}
                                        size="small"
                                        color="primary"
                                    />
                                </div>
                                <div style={{ paddingTop: 2 }}>Not yet started</div>
                            </div>
                        </div>
                        <div>
                            <div style={{ display: 'flex' }}>Actual Study End Date:</div>
                            <div style={{ width: 180 }}>
                                <CustomDatePicker
                                    disabled={!isPermission || getValue('isNotYetStarted')}
                                    value={getValue('actualStudyEndDate')}
                                    validators={getValue('isNotYetStarted') ? [] : ['isRequired']}
                                    forceErrorDisplay={forceErrorDisplay}
                                    isValidValue={(isValid) => {
                                        updateValidation('actualStudyEndDate', isValid);
                                    }}
                                    maxDate={new Date().getTime()}
                                    onChange={(date) => {
                                        updateValue('actualStudyEndDate', date);
                                    }}
                                />
                            </div>
                        </div>
                    </SEvenRow>

                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div>Planned Sample Size (Total):</div>
                            <div>{getValue('plannedSampleSize')}</div>
                        </div>
                        <div>
                            <div>No. of Completed*:</div>
                            <div>
                                <CustomTextField
                                    disabled={!isPermission}
                                    label="No. of Completed"
                                    value={getValue('noOfCompleted')}
                                    forceErrorDisplay={forceErrorDisplay}
                                    validators={['isRequired']}
                                    debounceChange
                                    // numberField
                                    style={{ width: '100%' }}
                                    isValidValue={(isValid) => {
                                        updateValidation('noOfCompleted', isValid);
                                    }}
                                    onChange={(e) => {
                                        updateValue('noOfCompleted', e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    </SEvenRow>
                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div>No. of Recruited*:</div>
                            <div>
                                <CustomTextField
                                    // numberField
                                    style={{ width: '100%' }}
                                    disabled={!isPermission}
                                    label="No. of Recruited"
                                    value={getValue('noOfRecruited')}
                                    forceErrorDisplay={forceErrorDisplay}
                                    validators={['isRequired']}
                                    debounceChange
                                    isValidValue={(isValid) => {
                                        updateValidation('noOfRecruited', isValid);
                                    }}
                                    onChange={(e) => {
                                        updateValue('noOfRecruited', e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    </SEvenRow>
                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>Any Withdrawal?:</div>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <CustomRadio
                                    disabled={!isPermission}
                                    value={getValue('anyWithdrawal')}
                                    onChange={(e) => {
                                        updateValue('anyWithdrawal', JSON.parse(e.target.value));

                                        if (!JSON.parse(e.target.value)) {
                                            clearKey(['noOfWithdrawnAfterRecruited', 'withdrawalReasons']);
                                        }
                                    }}
                                    isRequired
                                    isValidValue={(isValid) => {
                                        updateValidation('anyWithdrawal', isValid);
                                    }}
                                    forceErrorDisplay={forceErrorDisplay}
                                    operators={[
                                        { key: true, label: 'Yes' },
                                        { key: false, label: 'No' },
                                    ]}
                                />
                            </div>
                        </div>
                        <div>
                            <div>No. of Withdrawn after recruited*:</div>
                            <div>
                                <CustomTextField
                                    disabled={!isPermission || !getValue('anyWithdrawal')}
                                    value={getValue('noOfWithdrawnAfterRecruited')}
                                    forceErrorDisplay={forceErrorDisplay}
                                    debounceChange
                                    // numberField
                                    style={{ width: '100%' }}
                                    validators={!getValue('anyWithdrawal') ? [] : ['isRequired']}
                                    isValidValue={(isValid, isMaxLengthValid) => {
                                        updateValidation('noOfWithdrawnAfterRecruited', isValid);
                                    }}
                                    onChange={(e) => {
                                        updateValue('noOfWithdrawnAfterRecruited', e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    </SEvenRow>

                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div>Withdrawal reasons:</div>
                            <div>
                                <CustomTextField
                                    disabled={!isPermission || !getValue('anyWithdrawal')}
                                    label="Withdrawal reasons"
                                    value={getValue('withdrawalReasons')}
                                    forceErrorDisplay={forceErrorDisplay}
                                    validators={!getValue('anyWithdrawal') ? [] : ['isRequired']}
                                    maxLength={500}
                                    isTextArea
                                    debounceChange
                                    textAreaMinRow={3}
                                    isValidValue={(isValid, isMaxLengthValid) => {
                                        updateValidation('withdrawalReasons', isValid);
                                        updateMaxLength('withdrawalReasons', isMaxLengthValid);
                                    }}
                                    onChange={(e) => {
                                        updateValue('withdrawalReasons', e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    </SEvenRow>
                </div>

                <div style={{ margin: '10px 0px' }}>
                    <i>
                        *For multi-sites trial, please specify the total no. of subjects as well as the no. of subjects
                        in corresponding local site(s).
                    </i>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', marginTop: 20 }}>
                    <TextSubHeader style={{ marginRight: 20 }}>
                        3. Summary of Local Serious Adverse Events
                    </TextSubHeader>
                    <CustomRadio
                        disabled={!isPermission}
                        forceErrorDisplay={forceErrorDisplay}
                        isRequired
                        isValidValue={(isValid) => {
                            updateValidation('mandatorySeriousAdverseEvents', isValid);
                        }}
                        onChange={(e) => {
                            updateValue('mandatorySeriousAdverseEvents', JSON.parse(e.target.value));
                        }}
                        value={getValue('mandatorySeriousAdverseEvents')}
                        operators={[
                            {
                                key: true,
                                label: 'Yes',
                            },
                            {
                                key: false,
                                label: 'No/Not Applicable',
                            },
                        ]}
                    />
                </div>
                {isPermission && (
                    <CustomButton
                        disabled={!isPermission}
                        style={{ marginTop: 8 }}
                        onClick={() => {
                            setSelectedAdverseEventsRow(null);
                            setShowAdverseEventsDialog(true);
                        }}
                    >
                        Add
                    </CustomButton>
                )}
                <SSmallSection>
                    <CustomTable
                        disabled={!isPermission}
                        columns={adverseEventsColumns}
                        name={'mandatorySeriousAdverseEventsTable'}
                        rows={adverseEventsRows}
                        isEditAllow
                        isDeleteAllow
                        onAppFieldValidate={(name, value) => {
                            if (getValue('mandatorySeriousAdverseEvents') === true) {
                                updateValidation(name, value);
                            } else {
                                updateValidation(name, true);
                            }
                        }}
                        onDelete={handleAdverseEventsDelete}
                        onEdit={handleAdverseEventsEditClick}
                    />
                    {/* {adverseEventsRows.length > 0 && (
                   
                )} */}
                </SSmallSection>
                <div style={{ marginTop: 20, display: 'flex', alignItems: 'center' }}>
                    Does the Local SAE affect the study?
                    <div style={{ paddingLeft: 20, display: 'flex', alignItems: 'center' }}>
                        <CustomRadio
                            disabled={!isPermission}
                            isRequired={adverseEventsRows.length > 0}
                            onChange={(e) => {
                                updateValue('saeAffect', JSON.parse(e.target.value));

                                if (!JSON.parse(e.target.value)) {
                                    clearKey(['saeDetails']);
                                }
                            }}
                            forceErrorDisplay={forceErrorDisplay}
                            isValidValue={(isValid) => {
                                updateValidation('saeAffect', isValid);
                            }}
                            value={getValue('saeAffect')}
                            operators={[
                                {
                                    key: true,
                                    label: 'Yes',
                                },
                                {
                                    key: false,
                                    label: 'No',
                                },
                            ]}
                        />
                    </div>
                </div>
                <div style={{ marginTop: 10 }}>
                    <CustomTextField
                        disabled={!isPermission || !getValue('saeAffect')}
                        label="Please provide details"
                        value={getValue('saeDetails')}
                        forceErrorDisplay={forceErrorDisplay}
                        validators={getValue('saeAffect') ? ['isRequired'] : []}
                        maxLength={500}
                        isTextArea
                        debounceChange
                        textAreaMinRow={3}
                        isValidValue={(isValid, isMaxLengthValid) => {
                            updateValidation('saeDetails', isValid);
                            updateMaxLength('saeDetails', isMaxLengthValid);
                        }}
                        onChange={(e) => {
                            updateValue('saeDetails', e.target.value);
                        }}
                    />
                </div>

                <div style={{ marginTop: 20 }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <TextSubHeader>4. Complaints from Subjects</TextSubHeader>
                        <div style={{ paddingLeft: 20, display: 'flex', alignItems: 'center' }}>
                            <CustomRadio
                                disabled={!isPermission}
                                forceErrorDisplay={forceErrorDisplay}
                                isRequired
                                isValidValue={(isValid) => {
                                    updateValidation('complaintsFromSubjects', isValid);
                                }}
                                onChange={(e) => {
                                    updateValue('complaintsFromSubjects', JSON.parse(e.target.value));

                                    if (!JSON.parse(e.target.value)) {
                                        clearKey(['complaintsDetails']);
                                    }
                                }}
                                value={getValue('complaintsFromSubjects')}
                                operators={[
                                    {
                                        key: true,
                                        label: 'Yes',
                                    },
                                    {
                                        key: false,
                                        label: 'No',
                                    },
                                ]}
                            />
                        </div>
                    </div>
                    <div style={{ marginTop: 10 }}>
                        <CustomTextField
                            disabled={!isPermission || !getValue('complaintsFromSubjects')}
                            label="Summary of Complaints from Subjects"
                            value={getValue('complaintsDetails')}
                            forceErrorDisplay={forceErrorDisplay}
                            validators={getValue('complaintsFromSubjects') ? ['isRequired'] : []}
                            maxLength={500}
                            isTextArea
                            debounceChange
                            textAreaMinRow={6}
                            isValidValue={(isValid, isMaxLengthValid) => {
                                updateValidation('complaintsDetails', isValid);
                                updateMaxLength('complaintsDetails', isMaxLengthValid);
                            }}
                            onChange={(e) => {
                                updateValue('complaintsDetails', e.target.value);
                            }}
                        />
                    </div>
                </div>
                <div style={{ marginTop: 20 }}>
                    <TextSubHeader>5. Study Status</TextSubHeader>

                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <CustomRadio
                            disabled={!isPermission}
                            forceErrorDisplay={forceErrorDisplay}
                            isRequired
                            isValidValue={(isValid) => {
                                updateValidation('studyDuration', isValid);
                            }}
                            onChange={(e) => {
                                updateValue('studyDuration', e.target.value);
                            }}
                            value={getValue('studyDuration')}
                            operators={[
                                {
                                    customKey: 'completed',
                                    label: 'Completed',
                                },
                                {
                                    customKey: 'prematureTermination',
                                    label: 'Premature termination',
                                },
                            ]}
                        />
                    </div>
                </div>
                <SSection>
                    <TextSubHeader>
                        6. Summary of Study Outcomes and Significant Findings / Reasons for Termination
                    </TextSubHeader>
                    <SSmallSection>
                        <CustomTextField
                            disabled={!isPermission}
                            label="Summary of Study Outcomes and Significant Findings / Reasons for Termination"
                            value={getValue('terminationReason')}
                            forceErrorDisplay={forceErrorDisplay}
                            validators={['isRequired']}
                            maxLength={3000}
                            isTextArea
                            debounceChange
                            textAreaMinRow={6}
                            isValidValue={(isValid, isMaxLengthValid) => {
                                updateValidation('terminationReason', isValid);
                                updateMaxLength('terminationReason', isMaxLengthValid);
                            }}
                            onChange={(e) => {
                                updateValue('terminationReason', e.target.value);
                            }}
                        />
                    </SSmallSection>
                </SSection>
                <SSection>
                    <TextSubHeader>7. Translating Study Outcome into Clinical Practices</TextSubHeader>

                    <SSmallSection>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <CustomRadio
                                disabled={!isPermission}
                                forceErrorDisplay={forceErrorDisplay}
                                isRequired
                                isValidValue={(isValid) => {
                                    updateValidation('translationStudyOutcome', isValid);
                                }}
                                onChange={(e) => {
                                    updateValue('translationStudyOutcome', parseInt(e.target.value));

                                    if (parseInt(e.target.value) === 2) {
                                        clearKey(['describeWhichAspects']);
                                    }
                                }}
                                value={getValue('translationStudyOutcome')}
                                operators={[
                                    {
                                        key: 0,
                                        label: 'Yes, translated',
                                    },
                                    {
                                        key: 1,
                                        label: 'Yes, in progress',
                                    },
                                    {
                                        key: 2,
                                        label: 'No / Not applicable',
                                    },
                                ]}
                            />
                        </div>
                    </SSmallSection>

                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div>Details:</div>
                            <div>
                                <CustomTextField
                                    disabled={
                                        !isPermission ||
                                        getValue('translationStudyOutcome') === 2 ||
                                        checkNullOrUndefined(getValue('translationStudyOutcome'))
                                    }
                                    label="Describe which aspects of the study outcome have been/are being translated into clinical practices"
                                    value={getValue('describeWhichAspects')}
                                    forceErrorDisplay={forceErrorDisplay}
                                    validators={
                                        getValue('translationStudyOutcome') === 0 ||
                                        getValue('translationStudyOutcome') === 1
                                            ? ['isRequired']
                                            : []
                                    }
                                    maxLength={1000}
                                    isTextArea
                                    debounceChange
                                    textAreaMinRow={3}
                                    isValidValue={(isValid, isMaxLengthValid) => {
                                        updateValidation('describeWhichAspects', isValid);
                                        updateMaxLength('describeWhichAspects', isMaxLengthValid);
                                    }}
                                    onChange={(e) => {
                                        updateValue('describeWhichAspects', e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    </SEvenRow>
                </SSection>
                <SSection>
                    <TextSubHeader>
                        8. Publication of Research Study (Please attach the abstract if available)
                    </TextSubHeader>

                    <SSmallSection>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <CustomRadio
                                disabled={!isPermission}
                                forceErrorDisplay={forceErrorDisplay}
                                isRequired
                                isValidValue={(isValid) => {
                                    updateValidation('publication', isValid);
                                }}
                                onChange={(e) => {
                                    updateValue('publication', parseInt(e.target.value));

                                    if (parseInt(e.target.value) === 2) {
                                        clearKey([
                                            'indicateDetailsTitle',
                                            'indicateDetailsYear',
                                            'indicateDetailsNameOfYear',
                                        ]);
                                    }
                                }}
                                test
                                value={getValue('publication')}
                                operators={[
                                    {
                                        key: 0,
                                        label: 'Yes, published',
                                    },
                                    {
                                        key: 1,
                                        label: 'Yes, in progress',
                                    },
                                    {
                                        key: 2,
                                        label: 'No',
                                    },
                                ]}
                            />
                        </div>
                    </SSmallSection>

                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div>Title of published paper or to-be-published paper:</div>
                            <div>
                                <CustomTextField
                                    disabled={
                                        !isPermission ||
                                        getValue('publication') === 2 ||
                                        checkNullOrUndefined(getValue('publication'))
                                    }
                                    label="Title of published paper or to-be-published paper:"
                                    value={getValue('indicateDetailsTitle')}
                                    forceErrorDisplay={forceErrorDisplay}
                                    validators={
                                        getValue('publication') === 0 || getValue('publication') === 1
                                            ? ['isRequired']
                                            : []
                                    }
                                    maxLength={500}
                                    isTextArea
                                    debounceChange
                                    textAreaMinRow={3}
                                    isValidValue={(isValid, isMaxLengthValid) => {
                                        updateValidation('indicateDetailsTitle', isValid);
                                        updateMaxLength('indicateDetailsTitle', isMaxLengthValid);
                                    }}
                                    onChange={(e) => {
                                        updateValue('indicateDetailsTitle', e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    </SEvenRow>

                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div>Year of publication:</div>
                            <div>
                                <CustomTextField
                                    disabled={
                                        !isPermission ||
                                        getValue('publication') === 2 ||
                                        checkNullOrUndefined(getValue('publication'))
                                    }
                                    label="Year of publication"
                                    value={getValue('indicateDetailsYear')}
                                    forceErrorDisplay={forceErrorDisplay}
                                    validators={
                                        getValue('publication') === 0 || getValue('publication') === 1
                                            ? ['isRequired']
                                            : []
                                    }
                                    maxLength={500}
                                    isTextArea
                                    debounceChange
                                    textAreaMinRow={3}
                                    isValidValue={(isValid, isMaxLengthValid) => {
                                        updateValidation('indicateDetailsYear', isValid);
                                        updateMaxLength('indicateDetailsYear', isMaxLengthValid);
                                    }}
                                    onChange={(e) => {
                                        updateValue('indicateDetailsYear', e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    </SEvenRow>

                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div>Name of Journal:</div>
                            <div>
                                <CustomTextField
                                    disabled={
                                        !isPermission ||
                                        getValue('publication') === 2 ||
                                        checkNullOrUndefined(getValue('publication'))
                                    }
                                    label="Name of Journal:"
                                    value={getValue('indicateDetailsNameOfYear')}
                                    forceErrorDisplay={forceErrorDisplay}
                                    validators={
                                        getValue('publication') === 0 || getValue('publication') === 1
                                            ? ['isRequired']
                                            : []
                                    }
                                    maxLength={500}
                                    isTextArea
                                    debounceChange
                                    textAreaMinRow={3}
                                    isValidValue={(isValid, isMaxLengthValid) => {
                                        updateValidation('indicateDetailsNameOfYear', isValid);
                                        updateMaxLength('indicateDetailsNameOfYear', isMaxLengthValid);
                                    }}
                                    onChange={(e) => {
                                        updateValue('indicateDetailsNameOfYear', e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    </SEvenRow>
                </SSection>
                <SSection>
                    <TextSubHeader>9. Public Registration of Clinical Trial</TextSubHeader>

                    <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
                        This clinical trial is registered in a publicly available, free to access, searchable clinical
                        trial registry?
                        <div style={{ paddingLeft: 10, display: 'flex', alignItems: 'center' }}>
                            <CustomRadio
                                disabled={!isPermission}
                                onChange={(e) => {
                                    updateValue('registered', JSON.parse(e.target.value));

                                    if (!JSON.parse(e.target.value)) {
                                        clearKey(['registerName', 'registryIdentifier']);
                                    }
                                }}
                                value={getValue('registered')}
                                isRequired
                                isValidValue={(isValid) => {
                                    updateValidation('registered', isValid);
                                }}
                                forceErrorDisplay={forceErrorDisplay}
                                operators={[
                                    {
                                        key: true,
                                        label: 'Yes',
                                    },
                                    {
                                        key: false,
                                        label: 'No / Not Applicable',
                                    },
                                ]}
                            />
                        </div>
                    </div>
                    <SEvenRow style={{ marginTop: 10 }}>
                        <div>
                            <div>Name of the clinical trial registry:</div>
                            <div>
                                <CustomTextField
                                    disabled={!isPermission || !getValue('registered')}
                                    isTextArea
                                    debounceChange
                                    label="Name of the clinical trial registry"
                                    value={getValue('registerName')}
                                    forceErrorDisplay={forceErrorDisplay}
                                    validators={getValue('registered') ? ['isRequired'] : []}
                                    maxLength={500}
                                    isValidValue={(isValid, isMaxLengthValid) => {
                                        updateValidation('registerName', isValid);
                                        updateMaxLength('registerName', isMaxLengthValid);
                                    }}
                                    onChange={(e) => {
                                        updateValue('registerName', e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    </SEvenRow>
                    <SEvenRow style={{ marginTop: 20 }}>
                        <div>
                            <div>Registry identifier code / number for this clinical trial:</div>
                            <div>
                                <CustomTextField
                                    disabled={!isPermission || !getValue('registered')}
                                    isTextArea
                                    debounceChange
                                    label="Registry identifier code / number for this clinical trial:"
                                    value={getValue('registryIdentifier')}
                                    forceErrorDisplay={forceErrorDisplay}
                                    validators={getValue('registered') ? ['isRequired'] : []}
                                    maxLength={500}
                                    isValidValue={(isValid, isMaxLengthValid) => {
                                        updateValidation('registryIdentifier', isValid);
                                        updateMaxLength('registryIdentifier', isMaxLengthValid);
                                    }}
                                    onChange={(e) => {
                                        updateValue('registryIdentifier', e.target.value);
                                    }}
                                />
                            </div>
                        </div>
                    </SEvenRow>
                </SSection>
                <SSection>
                    <TextSubHeader>Upload Supplementary Document</TextSubHeader>
                    {isPermission && (
                        <CustomButton
                            disabled={!isPermission}
                            style={{ marginTop: 8 }}
                            onClick={() => setShowUploadDocDialog(true)}
                        >
                            Add
                        </CustomButton>
                    )}
                    <SSmallSection>
                        <CustomTable
                            disabled={!isPermission}
                            columns={uploadDocColumns}
                            rows={uploadDocRows}
                            isDeleteAllow
                            onDelete={handleUploadDocDelete}
                        />
                    </SSmallSection>
                </SSection>
            </>
        </GeneralPostApprovalLayout>
    );
};

export default FinalReport;
